import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package, ChevronRight, Filter, ChevronLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { ordersApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { formatPrice } from '../../utils/currency';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const OrdersPage = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const ordersPerPage = 10;
  const currentLang = i18n.language || 'en';
  const dateLocale = currentLang.startsWith('es') ? 'es-CO' : 'en-US';

  useEffect(() => {
    fetchOrders();
  }, [currentPage, filter]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      const data = await ordersApi.getOrders(currentPage, ordersPerPage, token);
      setOrders(data.orders || data || []);
      setTotalPages(data.pages || 1);
      setTotalOrders(data.total || 0);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast({
        title: t('common.error'),
        description: t('orders.error.load'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      processing: 'bg-blue-100 text-blue-800 border-blue-200',
      shipped: 'bg-purple-100 text-purple-800 border-purple-200',
      delivered: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const filteredOrders = orders.filter((order) => {
    if (filter === 'all') return true;
    return order.status === filter;
  });

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1); // Reset to page 1 when filter changes
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-center gap-2 mt-8 pb-4">
        {/* Previous Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {/* First Page */}
        {startPage > 1 && (
          <>
            <Button
              variant={currentPage === 1 ? 'default' : 'outline'}
              size="sm"
              onClick={() => handlePageChange(1)}
              className={currentPage === 1 ? 'bg-[#be1f1a] hover:bg-[#a71b17]' : ''}
            >
              1
            </Button>
            {startPage > 2 && <span className="px-2 text-gray-500">...</span>}
          </>
        )}

        {/* Page Numbers */}
        {pages.map((page) => (
          <Button
            key={page}
            variant={currentPage === page ? 'default' : 'outline'}
            size="sm"
            onClick={() => handlePageChange(page)}
            className={currentPage === page ? 'bg-[#be1f1a] hover:bg-[#a71b17]' : ''}
          >
            {page}
          </Button>
        ))}

        {/* Last Page */}
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="px-2 text-gray-500">...</span>}
            <Button
              variant={currentPage === totalPages ? 'default' : 'outline'}
              size="sm"
              onClick={() => handlePageChange(totalPages)}
              className={currentPage === totalPages ? 'bg-[#be1f1a] hover:bg-[#a71b17]' : ''}
            >
              {totalPages}
            </Button>
          </>
        )}

        {/* Next Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>

        {/* Page Info */}
        <span className="ml-4 text-sm text-gray-600">
          {t('productReviews.pageOf', { page: currentPage, total: totalPages })}
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#be1f1a]"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-[#2C2420]">{t('orders.title')}</h2>
          <p className="text-sm text-gray-500 mt-1">
            {totalOrders} {t('orders.totalOrders', { count: totalOrders })}
          </p>
        </div>
        
        <Select value={filter} onValueChange={handleFilterChange}>
          <SelectTrigger className="w-[180px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder={t('orders.filterByStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('orders.allOrders')}</SelectItem>
            <SelectItem value="pending">{t('orders.pending')}</SelectItem>
            <SelectItem value="processing">{t('orders.processing')}</SelectItem>
            <SelectItem value="shipped">{t('orders.shipped')}</SelectItem>
            <SelectItem value="delivered">{t('orders.delivered')}</SelectItem>
            <SelectItem value="cancelled">{t('orders.cancelled')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filteredOrders.length === 0 ? (
        <div className="text-center py-12">
          <Package className="h-20 w-20 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">
            {filter === 'all' ? t('orders.noOrders') : t('orders.noFilteredOrders')}
          </p>
          <Link to="/shop">
            <Button className="bg-[#be1f1a] hover:bg-[#a71b17]">
              {t('dashboard.startShopping')}
            </Button>
          </Link>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {filteredOrders.map((order) => (
              <Link key={order.id || order._id} to={`/account/orders/${order.orderNumber}`}>
                <div className="border rounded-lg p-6 hover:border-[#be1f1a] transition-colors cursor-pointer">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="font-semibold text-lg text-[#be1f1a]">#{order.orderNumber}</p>
                      <p className="text-sm text-gray-500">
                        {t('orders.orderedOn')} {new Date(order.createdAt).toLocaleDateString(dateLocale, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(order.status)}`}>
                      {t(`orders.${order.status}`, { defaultValue: order.status }).toUpperCase()}
                    </span>
                  </div>

                  <div className="flex gap-4 mb-4">
                    {order.items?.slice(0, 3).map((item, index) => (
                      <div key={index} className="w-16 h-20 bg-gray-100 rounded overflow-hidden">
                        <img
                          src={item.image || 'https://placehold.co/80x100'}
                          alt={item.name?.[currentLang] || item.name?.en || t('accountOrders.product')}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                    {order.items?.length > 3 && (
                      <div className="w-16 h-20 bg-gray-100 rounded flex items-center justify-center text-gray-500 text-sm">
                        +{order.items.length - 3}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between items-center pt-4 border-t">
                    <div>
                      <p className="text-sm text-gray-600">{order.items?.length || 0} {t('orders.items')}</p>
                      <p className="text-sm text-gray-600">{t('orders.payment')}: {t(`accountOrders.paymentStatus.${order.paymentStatus}`, { defaultValue: order.paymentStatus })}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-gray-500">{t('orders.total')}</p>
                        <p className="text-xl font-semibold text-[#2C2420]">
                          {formatPrice(order.total)}
                        </p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          
          {/* Pagination */}
          {renderPagination()}
        </>
      )}
    </div>
  );
};

export default OrdersPage;